import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { answerBlock } from 'actions';

import QuestionnaireType from 'common/questionnaires/QuestionnaireType';
import QuestionBlock from 'components/Questionnaires/QuestionBlock';
import Question from 'components/Questionnaires/Question';
import showQuestion from 'components/Questionnaires/questions';
import { QuestionGiinBlock, QuestionFtinBlock } from 'components/Questionnaires/blocks';
import { QuestionUsTinField } from 'components/Questionnaires/fields';
import { isEmptyObject, lengthValidator, validateQuestion, sameCharacterValidator, sequentialIntegerValidator, isBlockFullyValid, setValidators } from 'components/Questionnaires/validators';
import { jurisdictionIgaModel } from 'common/jurisdictions/jurisdictionsHelper';
import igaModel from 'common/forms/igaModel';

export const QuestionTaxIdentificationNumbersBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const { id, index, componentProps } = block;
  const { usTinProps, ftinProps } = componentProps || {};
  const { info: usTinInfo, required: usTinIsRequired } = usTinProps || {};
  const { required: ftinIsRequired } = ftinProps || {};
  const qAnswers = useSelector(state => state?.questionnaires?.formQuestionnaire?.answers);

  const blockIdentifier = useMemo(() => ((block && id) ? `${ id }${ index !== undefined ? (`.${ index }`) : '' }` : undefined), [index]);
  const [answer, setAnswer] = useState(block.answers ?? {});
  const [unvalidateBlock, setUnvalidateBlock] = useState(false);

  const jurisdictions = useSelector(state => state?.app?.staticData?.jurisdictions ?? []);

  const isGiinVisible = () => {
    const countryIgaModel = jurisdictionIgaModel(qAnswers?.nonReportingIgaFfi?.country, jurisdictions);

    if (qAnswers?.entityType?.ch4Status.toLowerCase() === 'nonreportingigaffi') {
      if (qAnswers?.nonReportingIgaFfi?.igaSubclassification === 'RdcFiSpInvtEntityControlledForeignCorp114715') {
        return true;
      }

      if (qAnswers?.nonReportingIgaFfi?.igaSubclassification === 'CdcFiFfiTrusteeDocumentedTrust' && qAnswers?.nonReportingIgaFfi?.isUSTrustee === 'No') {
        return true;
      }

      const validIgaSubClassifications = [
        'CdcFiFfiSpInvtEntityControlledForeignCorp', 'CdcFiFfiRegisteredQualifiedCreditCardIssuer', 'RdcFiRegisteredQualifiedCreditCardIssuer114715',
        'CdcFiFfiCollectiveInvtVehicle', 'RdcFiQualifiedCollectiveInvtVehicle114715', 'RdcFiFiLocalClientBase',
        'SmallFinancialInstitutionWithLocalClientBase', 'RdcFiSmallFiLocalClientBase'
      ];

      if (countryIgaModel === igaModel.Model2Iga) {
        return validIgaSubClassifications.includes(qAnswers?.nonReportingIgaFfi?.igaSubclassification);
      }
    }

    const validCh4Statuses = [
      'ParticipatingFfi', 'ReportingModel1Ffi', 'ReportingModel2Ffi', 'RegisteredDeemedCompliantFfi',
      'DirectReportingNffe', 'SponsoredFfi', 'SponsoredDirectReportingNffe',
    ];

    return validCh4Statuses.includes(qAnswers?.entityType?.ch4Status);
  };

  const isTinRequired = () => {
    if (typeof usTinIsRequired === 'boolean') {
      return usTinIsRequired;
    }
    if (typeof usTinIsRequired === 'function') {
      return usTinIsRequired(qAnswers);
    }
    return false;
  };

  const isFtinRequired = () => {
    if (typeof ftinIsRequired === 'boolean') {
      return ftinIsRequired;
    }
    if (typeof ftinIsRequired === 'function') {
      return ftinIsRequired(qAnswers);
    }
    return false;
  };

  const getUsTinInfo = () => {
    if (!usTinInfo) return 'questionnaire.usTin.tin.info';
    if (typeof usTinInfo === 'function') {
      return usTinInfo(qAnswers) ?? 'questionnaire.usTin.tin.info';
    }
    if (typeof usTinInfo === 'string') {
      return usTinInfo;
    }
    return 'questionnaire.usTin.tin.info';
  };

  const giinCustomParameters = {};
  giinCustomParameters.jurisdictions = jurisdictions;

  const [giin, setGiin] = useState({
    id: 'giin',
    component: QuestionGiinBlock,
    expandCollapse: false,
    className: 'pl-3 pr-3 pt-0 pb-0 mt-0 no-border nested',
    visible: () => isGiinVisible(),
  });

  const [ftin, setFtin] = useState({
    id: 'ftin',
    component: QuestionFtinBlock,
    expandCollapse: false,
    className: 'pl-3 pr-3 pt-0 pb-0 mt-0 no-border nested',
    visible: true,
    componentProps: {
      required: false
    },
  });

  const onBlockAnswer = (b, a, v) => {
    switch (b.id) {
      case 'giin': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setGiin(newBlock);
        setAnswer({ ...answer, giin: a });
        break;
      }
      case 'ftin': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setFtin(newBlock);
        setAnswer({ ...answer, ftin: a });
        break;
      }
      default:
        break;
    }
  };

  const [usTin, setUsTin] = useState({
    id: 'usTin',
    component: QuestionUsTinField,
    componentProps: {
      tinFormat: 'EIN',
      length: 9
    },
    label: 'questionnaire.usTin',
    info: {
      instructions: () => getUsTinInfo()
    },
    visible: true,
    validation: setValidators(isTinRequired(), [
      { condition: lengthValidator, message: 'questionnaire.usTin.tin.lengthErrorMessage' },
      { condition: sameCharacterValidator, message: 'questionnaire.usTin.tin.sameCharErrorMessage' },
      { condition: sequentialIntegerValidator, message: 'questionnaire.usTin.tin.sequentialErrorMessage' },
    ], 'questionnaire.usTin.tin.requiredErrorMessage'),
    answer: answer?.usTin
  });

  const onUsTinChange = (question, value, setAns = true) => {
    const newUsTin = { ...question, answer: value };
    validateQuestion(newUsTin);
    setUsTin(newUsTin);
    if (setAns) {
      setAnswer({ ...answer, usTin: value });
    }
  };

  const isBlockValid = () => {
    const subBlocks = [ftin];
    if (isGiinVisible()) {
      subBlocks.push(giin);
    }
    const questions = [usTin];
    const result = isBlockFullyValid(subBlocks, questions, block?.answers, qAnswers, !isGiinVisible());
    return result;
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isBlockValid()));
    } else {
      onAnswer(block, answer, isBlockValid());
    }
  }, [answer, qAnswers?.nonReportingIgaFfi, qAnswers?.entityType]);

  useEffect(() => {
    const validValue = isTinRequired() ? undefined : isBlockValid();
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, validValue));
    } else {
      onAnswer(block, answer, validValue);
    }
    setUnvalidateBlock(false);
  }, [unvalidateBlock === true]);

  useEffect(() => {
    let newAnswer = { ...answer };
    const giinVisible = isGiinVisible();
    const giinChanges = { validation: setValidators(isGiinVisible(), [{ condition: lengthValidator, message: 'questionnaire.giin.lengthErrorMessage' }], 'questionnaire.giin.errorMessage') };
    if (!giinVisible && answer.giin) {
      giinChanges.answer = undefined;
      giinChanges.isValid = undefined;
      giinChanges.errorMessages = undefined;
      newAnswer = { ...newAnswer, giin: undefined };
    }
    setGiin({ ...giin, ...giinChanges });

    const tinChanges = {
      validation: setValidators(isTinRequired(), [
        { condition: lengthValidator, message: 'questionnaire.usTin.tin.lengthErrorMessage' },
        { condition: sameCharacterValidator, message: 'questionnaire.usTin.tin.sameCharErrorMessage' },
        { condition: sequentialIntegerValidator, message: 'questionnaire.usTin.tin.sequentialErrorMessage' },
      ], 'questionnaire.usTin.tin.requiredErrorMessage'),
      info: {
        instructions: getUsTinInfo() ?? ''
      }
    };
    if (usTin?.validation?.conditions.length !== tinChanges?.validation?.conditions.length) {
      if (isTinRequired() && (usTin.answer === undefined || usTin.answer === '')) {
        tinChanges.answer = undefined;
        tinChanges.isValid = undefined;
        setUnvalidateBlock(true);
      } else {
        const targetValue = 'questionnaire.usTin.tin.requiredErrorMessage';
        const foundIndex = usTin?.errorMessages?.findIndex(message => message === targetValue);
        let usTinErrors = { ...usTin.errorMessages };

        if (foundIndex !== undefined && foundIndex !== -1) {
          usTinErrors = usTinErrors?.errorMessages?.splice(index, 1);
          tinChanges.errorMessages = usTinErrors?.length > 0 ? usTinErrors : undefined;
        }

        if ((usTinErrors === undefined || usTinErrors?.length === 0) && usTin.isValid === false) {
          if (usTin.answer === '' || usTin.answer === undefined) {
            tinChanges.isValid = undefined;
            setUnvalidateBlock(true);
          }
        }
      }
    }
    setUsTin({ ...usTin, ...tinChanges });

    const ftinChanges = {};
    ftinChanges.componentProps = { ...ftin.componentProps, required: isFtinRequired() };
    if (!ftin.visible && (answer.ftin || answer.ftinNotRequired)) {
      ftinChanges.answer = undefined;
      ftinChanges.isValid = undefined;
      ftinChanges.errorMessages = undefined;
      newAnswer = { ...newAnswer, ftin: undefined };
    }
    setFtin({ ...ftin, ...ftinChanges });

    setAnswer(newAnswer);
  }, [answer, qAnswers?.nonReportingIgaFfi, qAnswers?.entityType, qAnswers?.nonqualifiedIntermediary?.isQsl, qAnswers?.qualifiedIntermediary?.entity]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (!giin.value || isEmptyObject(answer.giin)) {
        const newBlock = { ...giin, ...{ isForceEdited: true } };
        setGiin(newBlock);
      }
      if (!ftin.value || isEmptyObject(answer.ftin)) {
        const newBlock = { ...ftin, ...{ isForceEdited: true } };
        setFtin(newBlock);
      }
      const newAnswer = { ...answer };
      if (!answer?.usTin) {
        onUsTinChange({ ...usTin, isForceEdited: true }, '', false);
        newAnswer.usTin = '';
      }
      setAnswer({ ...newAnswer });
    } else {
      if (giin.isForceEdited !== block.isForceEdited) {
        const newBlock = { ...giin, ...{ isForceEdited: block.isForceEdited } };
        setGiin(newBlock);
      }
      if (ftin.isForceEdited !== block.isForceEdited) {
        const newBlock = { ...ftin, ...{ isForceEdited: block.isForceEdited } };
        setFtin(newBlock);
      }
      if (usTin.isForceEdited !== block.isForceEdited) {
        setUsTin({ ...usTin, isForceEdited: block.isForceEdited });
      }
    }
  }, [block.isForceEdited]);

  return (
    <React.Fragment>
      { giin.visible() && <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ giin.id }` } block={ giin } onAnswer={ onBlockAnswer } /> }
      { showQuestion(usTin, block?.answers, qAnswers) && <Question key={ usTin.id } blockId={ blockIdentifier } question={ usTin } { ...usTin.componentProps } onChange={ (q, a) => onUsTinChange(q, a) } /> }
      { ftin.visible && <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ ftin.id }` } block={ ftin } onAnswer={ onBlockAnswer } /> }
    </React.Fragment>
  );
};

QuestionTaxIdentificationNumbersBlock.displayName = 'QuestionTaxIdentificationNumbersBlock';

QuestionTaxIdentificationNumbersBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
};

QuestionTaxIdentificationNumbersBlock.defaultProps = {
  onAnswer: null,
};

export default QuestionTaxIdentificationNumbersBlock;
